import React, { useState, useMemo, useEffect } from "react";
import { inject, observer } from "mobx-react";
import jsYml from "js-yaml";
import ScrollToBottom, {
  useScrollToBottom,
  useSticky,
} from "react-scroll-to-bottom";

import CircularProgress from "@material-ui/core/CircularProgress";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";

import gpt3Encoder from "./modules/gpt-3-encoder";

import initialChatMessages from "./configs/initial-messages.chat";
import systemChatMessages from "./configs/system-messages.chat";

import {
  makeKey,
  keyBy,
  isTrue,
  fitResponseToFunctions,
  generateUUID,
  formatList,
} from "./lib/utils";

import {
  CHAT_MESSAGE_TAGS,
  FIELD_TYPES,
  OPENAI_MODELS,
  OPENAI_MODEL_VARIANTS,
  STATUS_COLORS,
  TASK_NAMES,
  FIELD_TYPES_BY_FIELD_TYPE,
} from "./lib/constants";

import useChatModel from "./hooks/useChatModel";

import StyledButton from "./buttons/StyledButton";
import UserChat from "./components/UserChat";
import AssistantChat from "./components/AssistantChat";

import PacksTaskAction from "./tasks/PacksTaskAction";
import SheetsTaskAction from "./tasks/SheetsTaskAction";
import SubsheetsTaskAction from "./tasks/SubsheetsTaskAction";
import AddGeneralFieldsTaskAction from "./tasks/AddGeneralFieldsTaskAction";
import LookupsTaskAction from "./tasks/LookupsTaskAction";
import FormulaeTaskAction from "./tasks/FormulaeTaskAction";
import NavigationTaskAction from "./tasks/NavigationTaskAction";
import CreateDummyDataTaskAction from "./tasks/CreateDummyDataTaskAction";
import ShareSheetTaskAction from "./tasks/ShareSheetTaskAction";

import ChatInput from "./ChatInput";
import ChatIntro from "./ChatIntro";
import FunctionChat from "./components/FunctionChat";

import { ChatUpdateInput, ChatsContainer, LoadingDots } from "./styles";
import { ArrowDownward } from "@material-ui/icons";

import input from "./configs/input.json";
import input2 from "./configs/input-2.json";
import input3 from "./configs/input-3.json";
import input4 from "./configs/input-4.json";
import input5 from "./configs/input-5.json";
import input7 from "./configs/input-7.json";

const ChatDrawerHome = (props) => {
  const {
    themeColor,
    classes,
    apiKey,
    tokensState,
    settings,
    chatCompletionOptions,
    assistantConfig,
    assistantLogo,
    packIdFromRoute,
    sheetIdFromRoute,
    isChatLoading,
    inputRef,
    onNewChatRequest,
    onFetchChatResponse,
    onTokensStateChange,
    onClose,
    //
    assistantStore,
    chatStore,
    chatSettingsStore,
    chatFnsStore,
    chatHelpersStore,
    chatTaskStore,
    leftHoverMenuStore,
    userStore,
  } = props;

  const [text, setText] = useState("");
  const [updateText, setUpdateText] = useState("");

  const isLoading = useMemo(
    () => isChatLoading || chatFnsStore.loading,
    [chatFnsStore.loading, isChatLoading]
  );

  const chatModelHelper = useChatModel({
    models: OPENAI_MODELS,
    modelVariants: OPENAI_MODEL_VARIANTS,
    baseChatModel: settings.baseChatModel,
    useMatchingTokenModel: settings.useMatchingTokenModel,
  });

  const message = useMemo(() => {
    return text.trim();
  }, [text]);

  const initialMessages = useMemo(() => {
    const { chatConfig } = assistantConfig;

    const { include = [] } = chatConfig;
    let _initialMessages = [];

    if (include.includes("system-messages")) {
      _initialMessages = [...systemChatMessages];
    }
    if (include.includes("initial-messages")) {
      _initialMessages = [..._initialMessages, ...initialChatMessages];
    }

    return _initialMessages;
  }, []);

  const userDisplayName = userStore?.user?.first_name;

  const messagesToShow = useMemo(() => {
    return [...chatStore.messages].filter((m, i) => {
      m.originalIndex = i;
      return m.type === "message" || m.type === "response";
    });
  }, [chatStore.messages]);

  const messageStream =
    chatStore.response.functionCall.arguments || chatStore.response.content;

  const getLocalItemsToCount = () => {
    let itemsToCount = [...initialMessages];

    if (chatCompletionOptions.body.functions) {
      itemsToCount.push(chatCompletionOptions.body.functions);
    }

    return itemsToCount;
  };

  const getStaticItemsCount = ({ modelVariantName }) => {
    let count = 0;
    const items = ["system-messages", "initial-messages", "functions"];

    for (const item of items) {
      if (
        chatCompletionOptions.body.include &&
        chatCompletionOptions.body.include.includes(item) &&
        chatSettingsStore.chatConfig &&
        chatSettingsStore.chatConfig.tokensSizes[item] &&
        chatSettingsStore.chatConfig.tokensSizes[item][modelVariantName]
      ) {
        count +=
          chatSettingsStore.chatConfig.tokensSizes[item][modelVariantName];
      }
    }

    return count;
  };

  const getTokensCount = (objectOrArray) => {
    const messagesStr = JSON.stringify(objectOrArray);
    const encodedMessages = gpt3Encoder.encode(messagesStr);

    return encodedMessages.length;
  };

  const handleEditMessage = (originalIndex) => () => {
    chatStore.setUpdateMessageIndex(originalIndex);
    const message = chatStore.messages[originalIndex];
    setUpdateText(message.content);
  };

  const handleEditCancel = () => {
    chatStore.setUpdateMessageIndex(-1);
  };

  const handleTextChange = (e) => {
    setText(e.target.value);
  };

  const handleChangeUpdateText = (e) => {
    setUpdateText(e.target.value);
  };

  const _formSubmitHandler = async ({ message, include = {} }) => {
    try {
      chatStore.setLoading(true);

      chatStore.setUpdateMessageIndex(undefined);
      let nodeId = generateUUID(6);
      let parentId = chatStore.getParentNodeId();
      let meta_data = {};

      const queryMessage = {
        node_id: nodeId,
        parent_row_id: parentId,
        role: "user",
        content: message,
        type: "message",
        tag:
          chatStore.submitCount === 0
            ? CHAT_MESSAGE_TAGS.firstChatQuery
            : undefined,
      };

      chatStore.addChatMessage(queryMessage, false, false);

      const newMessages = [];

      const { token } = await assistantStore.getChatsAccessToken();

      if (!assistantStore.packs.length)
        await assistantStore.fetchAndLoadPacks();

      if (include[CHAT_MESSAGE_TAGS.packs]) {
        // 1. add all packs info
        const packs = assistantStore.packs
          .filter((pack) => !isTrue(pack.is_log_pack))
          .map((pack) => `${pack.name} (id: ${pack.id})`);

        newMessages.push({
          content: packs.length ? jsYml.dump({ packs }) : "Total packs: 0",
          role: "system",
          tag: CHAT_MESSAGE_TAGS.packs,
          type: "internal",
        });
      }

      if (packIdFromRoute) {
        // get pack info
        const pack = assistantStore.packsById[packIdFromRoute];
        if (pack) {
          if (include[CHAT_MESSAGE_TAGS.currentPack]) {
            newMessages.push({
              content: jsYml.dump({
                currentPack: `${pack.name} (id: ${pack.id})`,
              }),
              role: "system",
              tag: CHAT_MESSAGE_TAGS.currentPack,
              type: "internal",
              meta: { pack: { name: pack.name, id: pack.id } },
            });
            meta_data["pack"] = { name: pack.name, id: pack.id };
          }

          if (include[CHAT_MESSAGE_TAGS.sheetsInCurrentPack]) {
            const sheets = (leftHoverMenuStore.packLists || [])
              .filter((sheet) => !isTrue(sheet.is_log_sheet))
              .map((sheet) => `${sheet.name} (id: ${sheet.id})`);

            newMessages.push({
              content: sheets.length
                ? jsYml.dump({ sheetsInCurrentPack: sheets })
                : "total sheets in current pack: 0",
              role: "system",
              tag: CHAT_MESSAGE_TAGS.sheetsInCurrentPack,
              type: "internal",
            });
          }
        }
      }

      // check value only, not the type
      if (
        packIdFromRoute &&
        sheetIdFromRoute &&
        (!assistantStore.currentSheetId ||
          assistantStore.currentSheetId != sheetIdFromRoute)
      ) {
        let res = await assistantStore.fetchListConfig({
          list_id: sheetIdFromRoute,
        });

        if (res && res.data) {
          const fields = res.data.listData;
          const sheet = res.data.list;
          if (sheet) {
            assistantStore.setListConfigs(sheetIdFromRoute, res.data);

            if (include[CHAT_MESSAGE_TAGS.currentSheetInfo]) {
              const currentSheet = {
                name: sheet.name,
                id: sheet.id,
              };
              meta_data["sheet"] = { name: sheet.name, id: sheet.id };
              newMessages.push({
                content: jsYml.dump({
                  currentSheet,
                }),
                role: "system",
                tag: CHAT_MESSAGE_TAGS.currentSheetInfo,
                type: "internal",
                meta: { sheet: currentSheet },
              });
            }

            if (include[CHAT_MESSAGE_TAGS.fieldsInCurrentSheet]) {
              const fieldsInCurrentSheet =
                chatHelpersStore.getSheetFieldsInfoRecursively(fields);
              newMessages.push({
                content: fieldsInCurrentSheet.length
                  ? jsYml.dump({ fieldsInCurrentSheet })
                  : "Total fields in current sheet: 0",
                role: "system",
                tag: CHAT_MESSAGE_TAGS.fieldsInCurrentSheet,
                type: "internal",
              });
            }
          }
        }

        assistantStore.setCurrentSheetId(sheetIdFromRoute);
      }

      chatStore.addUserInputToLogs(message, meta_data, nodeId, parentId);

      // newMessages.push(queryMessage);
      chatStore.removeMessage(chatStore.messages.length - 1); // remove the temporarily added query message
      chatStore.addChatMessages(newMessages, true);
      chatStore.addChatMessages([queryMessage], false);

      const itemsToCount = [...getLocalItemsToCount(), ...chatStore.messages];
      const staticItemsCount = getStaticItemsCount({
        modelVariantName:
          chatModelHelper.model?.name || settings.baseChatModel?.name,
      });
      const tokensSize = getTokensCount(itemsToCount) + staticItemsCount;
      const model = chatModelHelper.getModel({ tokensSize });

      chatStore.increaseSubmitCount();

      if (!model) {
        onTokensStateChange("EXCEEDED");
        return;
      }

      if (tokensSize >= model.maxTokens * 0.85) {
        onTokensStateChange("WARNING");
      }

      onTokensStateChange(null);

      await onFetchChatResponse(
        {
          messages: [...initialMessages, ...chatStore.messages],
        },
        {
          model: model.name,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
    } catch (error) {
      console.log(`error->`, error);
    } finally {
      chatStore.setLoading(false);
    }
  };

  const mainChatInputSubmit = async () => {
    if (!message) {
      inputRef.current?.focus();
      return;
    }
    await _formSubmitHandler({
      message,
      include:
        chatStore.submitCount === 0
          ? {
              [CHAT_MESSAGE_TAGS.packs]: true,
              [CHAT_MESSAGE_TAGS.currentPack]: true,
              [CHAT_MESSAGE_TAGS.sheetsInCurrentPack]: true,
              [CHAT_MESSAGE_TAGS.currentSheetInfo]: true,
              [CHAT_MESSAGE_TAGS.fieldsInCurrentSheet]: true,
            }
          : {},
    });
    setText("");
  };

  const handleKeyDown = async (e) => {
    if (!e.shiftKey && (e.key === "Enter" || e.key === "NumpadEnter")) {
      await mainChatInputSubmit();
    }
  };

  const handleCopyExampleCommand = (command) => {
    setText(command);
    inputRef.current?.focus();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    await mainChatInputSubmit();
  };

  const _messageUpdateHandler = async () => {
    const message = updateText.trim();
    if (!message) return;

    const newMessages = [...chatStore.messages].slice(
      0,
      chatStore.updateMessageIndex
    );

    chatStore.setChatMessages(newMessages);

    chatStore.setSubmitCount(1);

    await _formSubmitHandler({ message });
  };

  const handleMessageUpdateSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    await _messageUpdateHandler();
  };

  const handleMessageUpdateKeyDown = async (e) => {
    const key = e.keyCode || e.which;
    if (key === 13 && !e.shiftKey) {
      await _messageUpdateHandler();
    }
  };

  
  const handleAddLog = async (content) => {
    await chatStore.addChatMessage({
      role: "assistant",
      type: "message",
      content: content,
    });
  };

  const onDummyDataStepComplete = (params) => {
    const { log, sheetsCount } = params;
    handleAddLog(log);
  };

 
  const handleSheetsNavigatorSheetClick = async ({ sheet, pack }) => {
    try {
      const payload = {
        sheetId: sheet.id,
        packId: pack.id,
      };

      await chatHelpersStore.updateSheetPackAfterUpdate(payload);
      chatHelpersStore.navigateToSheet(payload);

      chatTaskStore.reset();
      onClose();
    } catch (e) {
      console.log(`error navigation->`, e);
    }
  };

  const handleSheetsNavigatorCancel = () => {
    chatTaskStore.reset();
    onClose();
  };

  const renderChatInputPrompter = () => {
    if (chatSettingsStore.opened) return null;

    const isSubmitDisabled =
      isLoading ||
      !message ||
      (assistantConfig.useApiKey && !apiKey) ||
      tokensState === "EXCEEDED";

    const inputDisabled =
      isLoading ||
      (assistantConfig.useApiKey && !apiKey) ||
      tokensState === "EXCEEDED";

    return (
      <div className={classes.chatFormContainer}>
        <form className={classes.chatForm} onSubmit={handleSubmit}>
          <ChatInput
            rowsMin={1}
            rowsMax={4}
            autoFocus
            ref={inputRef}
            placeholder= {packIdFromRoute ? "e.g. Create a user sheet" : "e.g. Create a users management system"}
            value={text}
            disabled={inputDisabled}
            loading={isLoading}
            onKeyDown={handleKeyDown}
            onChange={handleTextChange}
            themeColor={themeColor}
            isSubmitDisabled={isSubmitDisabled}
            showNewChat={messagesToShow.length > 0 && !chatSettingsStore.opened}
            onNewChatClick={onNewChatRequest}
          />
        </form>
      </div>
    );
  };

  const renderMessageUpdateInput = () => {
    const inputDisabled = isLoading || (assistantConfig.useApiKey && !apiKey);
    return (
      <form
        style={{ position: "relative", width: "100%" }}
        noValidate
        onSubmit={handleMessageUpdateSubmit}
      >
        <ChatUpdateInput
          name="message"
          rowsMin={1}
          rowsMax={4}
          autoFocus
          placeholder="Write updated message"
          value={updateText}
          disabled={inputDisabled}
          onChange={handleChangeUpdateText}
          onKeyDown={handleMessageUpdateKeyDown}
        />
        <div className={classes.updateActionContainer}>
          <div />
          <div className={classes.updateAction}>
            <StyledButton onClick={handleEditCancel}>Cancel</StyledButton>
            <StyledButton
              disabled={!updateText || inputDisabled}
              type="submit"
              color="primary"
            >
              Submit
            </StyledButton>
          </div>
        </div>
      </form>
    );
  };

  const renderChatStreamResponse = () => {
    if (!isChatLoading && !messageStream) return null;

    if (
      chatStore.response.functionCall &&
      chatStore.response.functionCall.arguments
    ) {
      return (
        <FunctionChat
          assistantLogo={assistantLogo}
          content={messageStream}
          loading
        />
      );
    }

    return (
      <AssistantChat
        assistantLogo={assistantLogo}
        content={messageStream}
        loading
      />
    );
  };

  const renderTaskAction = () => {
    if (chatTaskStore.currentTaskIndex === -1) return null;
    const currentTask = chatTaskStore.tasks[chatTaskStore.currentTaskIndex];

    if (!currentTask) return null;

    switch (currentTask.type) {
      case TASK_NAMES.createPacks: {
        return (
          <PacksTaskAction
            packs={chatTaskStore.packs}
            onComplete={chatHelpersStore.handlePacksTaskComplete}
            component="ChatDrawerHome"
          />
        );
      }

      case TASK_NAMES.createSheets: {
        return (
          <SheetsTaskAction
            url={chatCompletionOptions.url}
            body={chatCompletionOptions.body}
            headers={chatCompletionOptions.headers}
            baseChatModel={settings.baseChatModel}
            models={OPENAI_MODELS}
            modelVariants={OPENAI_MODEL_VARIANTS}
            useMatchingTokenModel={settings.useMatchingTokenModel}
            //
            // params={chatTaskStore.queue[0].item.params}
            sheets={chatTaskStore.sheets}
            packId={chatTaskStore.packs?.[0]?.id}
            //
            onComplete={chatHelpersStore.handleSheetsTaskComplete}
            onFetchChatsAccessToken={assistantStore.getChatsAccessToken}
            component="ChatDrawerHome"
          />
        );
      }

      case TASK_NAMES.createSubsheets: {
        return (
          <SubsheetsTaskAction
            onRequestParams={chatHelpersStore.handleGetSubsheetsTaskParams}
            onComplete={chatHelpersStore.handleSubsheetsTaskComplete}
            component="ChatDrawerHome"
          />
        );
      }

      case TASK_NAMES.addGeneralFields: {
        return (
          <AddGeneralFieldsTaskAction
            onRequestParams={chatHelpersStore.handleGetAddGeneralFieldsTaskParams}
            onComplete={chatHelpersStore.handleAddGeneralFieldsTaskComplete}
            component="ChatDrawerHome"
          />
        );
      }

      case TASK_NAMES.addLookupFields: {
        return (
          <LookupsTaskAction
            onRequestParams={chatHelpersStore.handleGetLookupsTaskParams}
            onComplete={chatHelpersStore.handleLookupsTaskComplete}
            component="ChatDrawerHome"
          />
        );
      }

      case TASK_NAMES.addFormulaFields: {
        return (
          <FormulaeTaskAction
            onRequestParams={chatHelpersStore.handleGetFormulaeTaskParams}
            onComplete={chatHelpersStore.handleFormulaeTaskComplete}
            component="ChatDrawerHome"
          />
        );
      }

      case TASK_NAMES.sheetsNavigation: {
        return (
          <NavigationTaskAction
            onRequestParams={chatHelpersStore.handleGetNavigatorTaskParams}
            onSheetClick={handleSheetsNavigatorSheetClick}
            onCancel={handleSheetsNavigatorCancel}
            component="ChatDrawerHome"
          />
        );
      }

      case TASK_NAMES.createDummyData: {
        return (
          <CreateDummyDataTaskAction
            onRequestParams={chatHelpersStore.handleGetDummyDataTaskParams}
            onStepComplete={onDummyDataStepComplete}
            onComplete={chatHelpersStore.handleDummyDataTaskComplete}
            component="ChatDrawerHome"
          />
        );
      }

      case TASK_NAMES.shareSheet: {
        return (
          <ShareSheetTaskAction
            onRequestParams={chatHelpersStore.handleGetShareSheetTaskParams}
            onComplete={chatHelpersStore.handleShareSheetTaskComplete}
            component="ChatDrawerHome"
          />
        );
      }

      default: {
        return null;
      }
    }
  };

  const renderChatMessages = () => {
    return (
      <ChatsContainer>
        {messagesToShow.map((chat, index) => {
          if (chat.role === "user") {
            return (
              <UserChat
                displayName={userDisplayName}
                key={index}
                {...chat}
                showActions={!isLoading}
                editing={chat.originalIndex === chatStore.updateMessageIndex}
                onEditClick={handleEditMessage(chat.originalIndex)}
                renderEditingMessage={renderMessageUpdateInput}
              />
            );
          }

          if (chat.role === "function") {
            return (
              <FunctionChat
                key={index}
                assistantLogo={assistantLogo}
                {...chat}
              />
            );
          }

          return (
            <AssistantChat
              key={index}
              assistantLogo={assistantLogo}
              {...chat}
            />
          );
        })}
        {renderChatStreamResponse()}
        {renderTaskAction()}
      </ChatsContainer>
    );
  };

  const renderDrawerContent = () => {

    if (!messagesToShow.length) {
      return (
        <ChatIntro
          themeColor={themeColor}
          assistantConfig={assistantConfig}
          onCopyQuery={handleCopyExampleCommand}
        />
      );
    }

    return renderChatMessages();
  };

  const renderAssistantInfoMessage = () => {
    if (assistantConfig.useApiKey && !apiKey) {
      return (
        <div
          style={{
            backgroundColor: STATUS_COLORS.critical.background,
            color: STATUS_COLORS.critical.color,
            padding: "8px 16px",
          }}
        >
          <p style={{ margin: 0 }}>
            No API key provided. Please add the key from chat settings.
          </p>
        </div>
      );
    }

    if (tokensState === "EXCEEDED") {
      return (
        <div
          style={{
            backgroundColor: STATUS_COLORS.critical.background,
            color: STATUS_COLORS.critical.color,
            padding: "8px 16px",
          }}
        >
          <p>
            Seems like the messages are too huge to make an http request. Please
            try running a fresh chat or edit above messages.
          </p>
          <div>
            <Button
              variant="contained"
              color="primary"
              disableElevation
              onClick={onNewChatRequest}
            >
              Start a new chat
            </Button>
          </div>
        </div>
      );
    }

    if (tokensState === "WARNING") {
      return (
        <div
          style={{
            backgroundColor: STATUS_COLORS.caution.background,
            color: STATUS_COLORS.caution.color,
            padding: "8px 16px",
          }}
        >
          <p>
            Seems like the your messages are getting bigger. We might not be
            able to process it if more messages are added due to limit on tokens
            size.
          </p>
          <div>
            <Button
              variant="contained"
              color="primary"
              disableElevation
              onClick={onNewChatRequest}
            >
              Start a new chat
            </Button>
          </div>
        </div>
      );
    }
  };

  const renderBottomContent = () => {
    return (
      <>
        {renderAssistantInfoMessage()}
        {renderChatInputPrompter()}
      </>
    );
  };

  const renderChatDrawerHome = () => {
    return (
      <>
        <ScrollToBottom
          className={classes.chatsContainer}
          scrollViewClassName={classes.chatsInnerContainer}
          followButtonClassName="scrollToBottomButton"
        >
          <Content>{renderDrawerContent()}</Content>
          <div id="scroll-to-bottom-chat-drawer-home" style={{ paddingBottom: "0rem" }}></div>
          {isLoading && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
              }}
            >
              <LoadingDots className="loading-dots-chat-dialog" />
            </div>
          )}
        </ScrollToBottom>
        {renderBottomContent()}
      </>
    );
  };

  const run = (input) => {
    const fns = {};
    fitResponseToFunctions(input, fns);

    const tasks = [];

    if (input.packName) {
      if (fns[TASK_NAMES.createPacks] && fns[TASK_NAMES.createPacks].length) {
        tasks.push({
          type: TASK_NAMES.createPacks,
          data: [{ packName: input.packName }],
        });
      }
    }

    if (fns[TASK_NAMES.createSheets] && fns[TASK_NAMES.createSheets].length) {
      tasks.push({
        type: TASK_NAMES.createSheets,
        data: {
          packName: input.packName,
          sheets: fns[TASK_NAMES.createSheets],
        },
      });
    }

    if (fns[TASK_NAMES.createSubsheets]) {
      tasks.push({
        type: TASK_NAMES.createSubsheets,
        data: fns[TASK_NAMES.createSubsheets],
      });
    }

    if (
      fns[TASK_NAMES.addLookupFields] &&
      fns[TASK_NAMES.addLookupFields].length
    ) {
      tasks.push({
        type: TASK_NAMES.addLookupFields,
        data: fns[TASK_NAMES.addLookupFields],
      });
    }

    if (
      fns[TASK_NAMES.addFormulaFields] &&
      fns[TASK_NAMES.addFormulaFields].length
    ) {
      tasks.push({
        type: TASK_NAMES.addFormulaFields,
        data: fns[TASK_NAMES.addFormulaFields],
      });
    }

    chatTaskStore.setTasks(tasks);
    chatTaskStore.updateTaskIndex(0);
  };

  return renderChatDrawerHome();
};

export const Content = ({ children }) => {
  const scrollToBottom = useScrollToBottom();
  const [sticky] = useSticky();
  return (
    <>
      {children}
      {!sticky && (
        <IconButton onClick={scrollToBottom} className="gradient-background">
          <ArrowDownward style={{ color: "white" }} />
        </IconButton>
      )}
    </>
  );
};

export default inject(({ store }) => {
  return {
    assistantStore: store.assistantStore,
    chatStore: store.chatStore,
    chatSettingsStore: store.chatSettingsStore,
    chatFnsStore: store.chatFnsStore,
    chatHelpersStore: store.chatHelpersStore,
    chatTaskStore: store.chatTaskStore,
    leftHoverMenuStore: store.leftHoverMenuStore,
    userStore: store.userStore,
  };
})(observer(ChatDrawerHome));
